import { Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { PRESET_MAX_PAGE_SIZE, PRESET_TYPE_LABELS } from '../constants';
import { shortsPresetService } from '../services/shortsPresetService';
import { ShortsPresetInterface } from '../types/shortsPresetType';

interface PresetSelectProps {
  /** Supplied by Form.Item. `null`/`undefined` means no preset is linked. */
  value?: number | null;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
}

/**
 * Preset picker for a bebridge playlist — the order carried by its work-order feed.
 * Options are platform-scoped by the backend, so a cross-platform selection is not
 * reachable from normal UI.
 *
 * Known limit: loads a single page (max 100). Presets beyond that are not listed.
 */
const PresetSelect: React.FC<PresetSelectProps> = ({ value, onChange, disabled }) => {
  const [presets, setPresets] = useState<ShortsPresetInterface[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPresets = async () => {
      setLoading(true);
      try {
        const response = await shortsPresetService.fetchPresets(
          new URLSearchParams({ page_size: String(PRESET_MAX_PAGE_SIZE) })
        );
        if (!cancelled) {
          setPresets(response.results ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch Shorts presets:', error);
          setPresets([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPresets();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Select
      allowClear
      showSearch
      optionFilterProp="label"
      loading={loading}
      disabled={disabled}
      placeholder="None"
      // allowClear yields undefined; normalize to null so the PATCH clears the link.
      value={value ?? undefined}
      onChange={next => onChange?.(next ?? null)}
      options={presets.map(preset => ({
        value: preset.id,
        label:
          `${preset.name} (${PRESET_TYPE_LABELS[preset.type] ?? preset.type}` +
          `, ${preset.aspect_ratio}, ×${preset.count}` +
          `, ${preset.min_duration}–${preset.max_duration}s)`,
      }))}
    />
  );
};

export default PresetSelect;
